import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Switch,
  Box,
  MenuItem,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import { accounts, lists } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const [accountList, setAccountList] = useState([]);
  const [listList, setListList] = useState([]);
  const [openAccountDialog, setOpenAccountDialog] = useState(false);
  const [openListDialog, setOpenListDialog] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [listData, setListData] = useState({
    externalId: '',
    accountId: '',
    status: 'enabled'
  });
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingList, setEditingList] = useState(null);
  const [error, setError] = useState('');
  const { logout } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accountsRes, listsRes] = await Promise.all([
        accounts.getAll(),
        lists.getAll()
      ]);
      setAccountList(accountsRes.data);
      setListList(listsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data. Please try again.');
    }
  };

  const handleAccountSubmit = async () => {
    try {
      if (editingAccount) {
        await accounts.update(editingAccount._id, accountName);
      } else {
        await accounts.create(accountName);
      }
      setOpenAccountDialog(false);
      setAccountName('');
      setEditingAccount(null);
      fetchData();
    } catch (error) {
      console.error('Error saving account:', error);
      setError('Failed to save account. Please try again.');
    }
  };

  const handleListSubmit = async () => {
    try {
      if (!editingList && !listData.accountId) {
        setError('Please select an account');
        return;
      }

      if (!listData.externalId) {
        setError('Please enter an external ID');
        return;
      }

      if (editingList) {
        await lists.update(editingList._id, listData.externalId, listData.status);
      } else {
        await lists.create(listData.externalId, listData.accountId, listData.status);
      }
      setOpenListDialog(false);
      setListData({ externalId: '', accountId: '', status: 'enabled' });
      setEditingList(null);
      fetchData();
    } catch (error) {
      console.error('Error saving list:', error);
      setError('Failed to save list. Please try again.');
    }
  };

  const handleDeleteAccount = async (id) => {
    try {
      await accounts.delete(id);
      fetchData();
    } catch (error) {
      console.error('Error deleting account:', error);
      setError('Failed to delete account. Please try again.');
    }
  };

  const handleDeleteList = async (id) => {
    try {
      await lists.delete(id);
      fetchData();
    } catch (error) {
      console.error('Error deleting list:', error);
      setError('Failed to delete list. Please try again.');
    }
  };

  const updateListStatus = async (list, newStatus) => {
    try {
      await lists.update(list._id, list.externalId, newStatus);
      // Update the list in the local state immediately
      setListList(prevLists => 
        prevLists.map(l => 
          l._id === list._id ? { ...l, status: newStatus } : l
        )
      );
    } catch (error) {
      console.error('Error updating list status:', error);
      setError('Failed to update list status. Please try again.');
      return false;
    }
    return true;
  };

  const handleStatusToggle = async (list, event) => {
    const newStatus = event.target.checked ? 'enabled' : 'disabled';
    const success = await updateListStatus(list, newStatus);
    if (!success) {
      // Revert the switch if update failed
      event.target.checked = list.status === 'enabled';
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4">List monitoring</Typography>
        <Button variant="outlined" color="secondary" onClick={logout}>
          Logout
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item style={{ width: '250px' }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Accounts</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingAccount(null);
                  setAccountName('');
                  setOpenAccountDialog(true);
                }}
              >
                Add Account
              </Button>
            </Box>
            <List>
              {accountList.map((account) => (
                <ListItem key={account._id}>
                  <ListItemText primary={account.name} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setEditingAccount(account);
                        setAccountName(account.name);
                        setOpenAccountDialog(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteAccount(account._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid item style={{ flex: 1 }}>
          <Paper sx={{ p: 2, minHeight: '500px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontSize: '1.5rem' }}>Lists</Typography>
              <Button
                startIcon={<AddIcon />}
                size="large"
                onClick={() => {
                  setEditingList(null);
                  setListData({ externalId: '', accountId: '', status: 'enabled' });
                  setOpenListDialog(true);
                }}
              >
                Add List
              </Button>
            </Box>
            <List sx={{ '& .MuiListItem-root': { py: 2 } }}>
              {listList.map((list) => (
                <ListItem key={list._id}>
                  <ListItemText
                    primary={<Typography variant="h6">{list.externalId}</Typography>}
                    secondary={
                      <Typography variant="subtitle1" color="text.secondary">
                        Account: {list.account?.name || 'Unknown Account'}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Switch
                      checked={list.status === 'enabled'}
                      onChange={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        const newStatus = event.target.checked ? 'enabled' : 'disabled';
                        lists.update(list._id, list.externalId, newStatus)
                          .then(() => {
                            setListList(prevLists => 
                              prevLists.map(l => 
                                l._id === list._id ? { ...l, status: newStatus } : l
                              )
                            );
                          })
                          .catch((error) => {
                            console.error('Error updating list status:', error);
                            setError('Failed to update list status. Please try again.');
                            event.target.checked = list.status === 'enabled';
                          });
                      }}
                      inputProps={{ 'aria-label': 'Toggle list status' }}
                      size="medium"
                    />
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setEditingList(list);
                        setListData({
                          externalId: list.externalId,
                          status: list.status
                        });
                        setOpenListDialog(true);
                      }}
                      size="large"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteList(list._id)}
                      size="large"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Account Dialog */}
      <Dialog open={openAccountDialog} onClose={() => setOpenAccountDialog(false)}>
        <DialogTitle>{editingAccount ? 'Edit Account' : 'Add Account'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Account Name"
            fullWidth
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAccountDialog(false)}>Cancel</Button>
          <Button onClick={handleAccountSubmit} variant="contained">
            {editingAccount ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* List Dialog */}
      <Dialog open={openListDialog} onClose={() => setOpenListDialog(false)}>
        <DialogTitle>{editingList ? 'Edit List' : 'Add List'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="External ID"
            fullWidth
            value={listData.externalId}
            onChange={(e) => setListData({ ...listData, externalId: e.target.value })}
            required
          />
          {!editingList && (
            <TextField
              select
              margin="dense"
              label="Account"
              fullWidth
              value={listData.accountId}
              onChange={(e) => setListData({ ...listData, accountId: e.target.value })}
              required
            >
              {accountList.map((account) => (
                <MenuItem key={account._id} value={account._id}>
                  {account.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            margin="dense"
            label="Status"
            fullWidth
            value={listData.status}
            onChange={(e) => setListData({ ...listData, status: e.target.value })}
            required
          >
            <MenuItem value="enabled">Enabled</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenListDialog(false)}>Cancel</Button>
          <Button onClick={handleListSubmit} variant="contained">
            {editingList ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard; 